// The Compliance hub's address (F18 UF-07) — which tab, each tab's filter and
// the open record live in the URL, so a tile, a notification or another page
// can open the hub on the right rows, and Back returns there.
//
//   /compliance?tab=checklists&runs=overdue&run=<uuid>
//   /compliance?tab=equipment&cal=due_30d&equip=<uuid>
//   /compliance?tab=renewals&kind=INSURANCE&status=all
//   /compliance?tab=documents&cert=<uuid>&section=<uuid>
//   /compliance?tab=ncs                      (an NC is handed over in router state, #3139)
//
// Every param below belongs to ONE tab. A tab switch clears the params of the
// tab it leaves, so `?tab=hse&run=…` can never carry a stale open record into
// the next tab or the next refresh.
//
// With no `tab` in the address the device's last tab still opens (the stored
// value), and every existing `?tab=` link keeps working: the address only ever
// adds to it.

import { useEffect } from 'react'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlState, useUrlPatch, oneOf } from '@/hooks/useUrlState'

/** The params a tab owns. `tab` itself is not in it — it is the switch. */
export const COMPLIANCE_TAB_PARAMS = [
  // Checklists: Runs/Templates, the runs filter, the open run.
  'view', 'runs', 'run',
  // Checklists scoped to one template and window (an audit's checklist record).
  'template', 'from', 'to',
  // Equipment: the calibration filter, the open item.
  'cal', 'equip',
  // Renewals: the kind and status filters.
  'kind', 'status',
  // Documents: the certificate and folder (UF-09 adds `doc`).
  'cert', 'section', 'doc',
  // NCs: a legacy `?nc=` link (the live hand-over is router state, #3139).
  'nc',
] as const

/** Renewals' "Due ≤ 30 days" filter (`?status=due30`): open items in these
 *  bands. The dashboard tile counts exactly this set and opens exactly this
 *  list, so the two cannot disagree. */
export const RENEWALS_DUE_STATUS = 'due30'
export const RENEWALS_DUE_BANDS = ['expired', '7', '30'] as const

/** The one patch a tab switch writes: the tab, every other tab param cleared,
 *  then `extra` (a tile opening a tab ON a filter). */
export function tabSwitchPatch(
  tab: string,
  extra: Record<string, string> = {},
): Record<string, string | null> {
  const patch: Record<string, string | null> = { tab }
  for (const k of COMPLIANCE_TAB_PARAMS) patch[k] = null
  return { ...patch, ...extra }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** `parse` for an open-record id. A hand-edited `?run=abc` must not reach
 *  PostgREST (22P02 → a red card for a record nobody asked about). */
export function uuidParam(raw: string): string | null {
  return UUID_RE.test(raw) ? raw : null
}

/** The tab to show: the address wins; with none, the device's last tab; a
 *  stored value from an older build that is no longer a tab falls to the
 *  dashboard. */
export function resolveTab<T extends string>(
  fromUrl: T | '',
  stored: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  if (fromUrl) return fromUrl
  return typeof stored === 'string' && (allowed as readonly string[]).includes(stored) ? (stored as T) : fallback
}

/**
 * The hub's tab: read from the address, the device's last tab when the
 * address names none. `openTab(t, extra)` writes ONE replace — the tab, the
 * other tabs' params cleared, then `extra` (a tile opening a tab on a filter:
 * `openTab('checklists', { runs: 'overdue' })`). It also stores the tab, so a
 * later visit with no `?tab=` opens there.
 */
export function useComplianceTab<T extends string>(
  storageKey: string,
  allowed: readonly T[],
  fallback: T,
): { tab: T; openTab: (t: T, extra?: Record<string, string>) => void } {
  const [stored, setStored] = usePersistedState<T>(storageKey, fallback)
  const [urlTab] = useUrlState<T | ''>('tab', '', oneOf<T>(...allowed))
  const patch = useUrlPatch()
  const tab = resolveTab(urlTab, stored, allowed, fallback)
  // A tab that arrived by link becomes the device's last tab too.
  useEffect(() => {
    if (urlTab && urlTab !== stored) setStored(urlTab)
  }, [urlTab]) // eslint-disable-line react-hooks/exhaustive-deps
  const openTab = (t: T, extra?: Record<string, string>) => {
    // Re-tapping the tab you are on keeps its filter and open record.
    if (t === tab && !extra) return
    setStored(t)
    patch(tabSwitchPatch(t, extra))
  }
  return { tab, openTab }
}
